/**
 * A project's worktrees on this device (RFC 0277 S3, S11), as the git server
 * names them: by folder and branch, never by absolute path.
 *
 * A service because two features read it: the files folder picks one to list,
 * and the composer's placement menu picks one for a session to work in.
 */

import { useCallback, useEffect, useState } from 'react'
import { fetchVcsFromServer } from '../../sdk/vcs'

export interface CodeWorktree {
  branch?: string
  name: string
  /** The main checkout, the one the others were added from. */
  main: boolean
  /** The commit it has out. */
  head?: string
}

export async function listProjectWorktrees(
  projectId: string,
  sourceId?: string,
): Promise<CodeWorktree[]> {
  return (await fetchVcsFromServer(
    'worktrees',
    [],
    projectId,
    sourceId,
  )) as CodeWorktree[]
}

/** Check a branch out in a new worktree on this device (RFC 0277 S4), or,
 *  with `from`, start `branch` there as a new branch (S14). */
export async function addProjectWorktree(
  projectId: string,
  branch: string,
  from?: string,
  sourceId?: string,
): Promise<CodeWorktree> {
  return (await fetchVcsFromServer(
    'addWorktree',
    from ? [branch, from] : [branch],
    projectId,
    sourceId,
  )) as CodeWorktree
}

/** The project's worktrees, or [] while unknown or when there is no git, and
 *  a way to ask again after one is added. */
export function useProjectWorktrees(
  projectId: string | undefined,
  list: (projectId: string) => Promise<CodeWorktree[]> = listProjectWorktrees,
  sourceId?: string,
): { trees: CodeWorktree[]; reload: () => void } {
  const [trees, setTrees] = useState<CodeWorktree[]>([])
  const [asked, setAsked] = useState(0)
  // `asked` is a trigger, not a value the read uses.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reload re-reads
  useEffect(() => {
    if (!projectId) return
    let live = true
    ;(sourceId
      ? listProjectWorktrees(projectId, sourceId)
      : list(projectId)
    ).then(
      (t) => {
        if (live) setTrees(t)
      },
      () => {
        if (live) setTrees([])
      },
    )
    return () => {
      live = false
    }
  }, [projectId, list, sourceId, asked])
  const reload = useCallback(() => setAsked((n) => n + 1), [])
  return { trees, reload }
}
